import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const performancePosters = ($) => {
	// Treat each block as an independent poster sequence and pagination instance.
	const blockEls = $(".performance-posters-block").toArray();
	if (!blockEls.length) return [];

	// Motion-sensitive users still get working pagination without entrance effects.
	const prefersReducedMotion = window.matchMedia(
		"(prefers-reduced-motion: reduce)",
	).matches;
	// Keep references to every GSAP control created by this initializer.
	const controls = [];

	blockEls.forEach((blockEl) => {
		const cards = $(blockEl).find(".performance-poster").toArray();
		// Store entrance tweens by card so pagination clicks can start them directly.
		const entranceTweens = new Map();
		const contentEls = cards
			.map((card) => $(card).find(".performance-poster__container").get(0))
			.filter(Boolean);
		const currentEl = $(blockEl)
			.find(".performance-posters-block__pagination-current")
			.get(0);
		const totalEl = $(blockEl)
			.find(".performance-posters-block__pagination-total")
			.get(0);
		const nextButtonEl = $(blockEl)
			.find(".performance-posters-block__pagination-next")
			.get(0);

		if (!cards.length) return;

		// Remove existing poster triggers and animations before initializing again.
		ScrollTrigger.getAll().forEach((trigger) => {
			if (cards.includes(trigger.trigger)) {
				trigger.kill();
			}
		});
		gsap.killTweensOf(contentEls);

		if (prefersReducedMotion) {
			gsap.set(contentEls, { autoAlpha: 1, x: 0 });
		} else {
			cards.forEach((card) => {
				const contentEl = $(card)
					.find(".performance-poster__container")
					.get(0);
				if (!contentEl) return;

				// Match the entrance direction to the card's desktop text orientation.
				const entersFromRight = contentEl.classList.contains(
					"performance-poster__container--right",
				);

				// Manual scrolling plays this tween when the card reaches its trigger point.
				const entranceTween = gsap.fromTo(
					contentEl,
					{ autoAlpha: 0, x: entersFromRight ? 48 : -48 },
					{
						autoAlpha: 1,
						x: 0,
						duration: 1.1,
						ease: "power2.out",
						scrollTrigger: {
							trigger: card,
							start: "top center-=150",
							once: true,
						},
					},
				);

				entranceTweens.set(card, entranceTween);
				controls.push(entranceTween);
			});
		}

		if (!currentEl || !totalEl || !nextButtonEl) return;

		// currentIndex is shared by scroll tracking and the next-card click handler.
		let currentIndex = 0;
		let isPaginating = false;

		// Update visible status, button availability, and its accessible destination.
		const updatePagination = (index) => {
			currentIndex = index;
			currentEl.textContent = String(index + 1);
			totalEl.textContent = String(cards.length);

			const isLastCard = index === cards.length - 1;
			nextButtonEl.disabled = isPaginating || isLastCard;
			nextButtonEl.setAttribute(
				"aria-label",
				isLastCard
					? "No next performance"
					: `Go to next performance, ${index + 2} of ${cards.length}`,
			);
		};

		updatePagination(0);

		// Change the active card when its top crosses the viewport center.
		cards.forEach((card, index) => {
			controls.push(
				ScrollTrigger.create({
					trigger: card,
					start: "top center",
					onEnter: () => {
						if (!isPaginating) updatePagination(index);
					},
					onLeaveBack: () => {
						if (!isPaginating) updatePagination(Math.max(index - 1, 0));
					},
				}),
			);
		});

		$(nextButtonEl)
			.off("click.performancePostersPagination")
			.on("click.performancePostersPagination", () => {
				if (isPaginating) return;

				const nextIndex = currentIndex + 1;
				const nextCard = cards[nextIndex];
				if (!nextCard) return;

				if (prefersReducedMotion) {
					updatePagination(nextIndex);
					nextCard.scrollIntoView({ behavior: "instant", block: "start" });
					return;
				}

				// Hold the reveal until the destination is visible, rather than
				// spending its animation time while the page is still scrolling.
				const entranceTween = entranceTweens.get(nextCard);
				entranceTween?.scrollTrigger?.kill(false, true);
				entranceTween?.pause(0);
				isPaginating = true;
				updatePagination(nextIndex);

				let scrollTween;
				const cancelPagination = () => scrollTween?.kill();
				const cancelOnScrollKey = (event) => {
					if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(event.key)) {
						cancelPagination();
					}
				};
				const finishPagination = () => {
					window.removeEventListener("wheel", cancelPagination);
					window.removeEventListener("touchmove", cancelPagination);
					window.removeEventListener("keydown", cancelOnScrollKey);
					isPaginating = false;
					entranceTween?.play(0);
					// Reconcile the counter if the user interrupted the scroll.
					let visibleIndex = 0;
					cards.forEach((card, index) => {
						if (card.getBoundingClientRect().top <= window.innerHeight / 2) {
							visibleIndex = index;
						}
					});
					updatePagination(visibleIndex);
				};

				// Mobile viewport adjustments can look like external scrolling to
				// autoKill. Only cancel for deliberate user input instead.
				scrollTween = gsap.to(window, {
					duration: 0.9,
					ease: "power2.inOut",
					scrollTo: {
						y: nextCard,
						autoKill: false,
					},
					onComplete: finishPagination,
					onInterrupt: finishPagination,
				});
				window.addEventListener("wheel", cancelPagination, { passive: true });
				window.addEventListener("touchmove", cancelPagination, { passive: true });
				window.addEventListener("keydown", cancelOnScrollKey);
				controls.push(scrollTween);
			});
	});

	return controls;
};

export default performancePosters;
